/**
 * "Variáveis do contrato" + "Fórmula de cálculo (USD → BRL)" — `2j`.
 *
 * Usado três vezes na mesma tela: painel de parâmetros do contrato, formulário de
 * aditivo e `novo contrato`. As variáveis e a fórmula são do CONJUNTO de
 * parâmetros, então cada aditivo carrega os seus (BR-21, decidido por Fábio em
 * 17/09/2026).
 *
 * A fórmula deixou de ser texto livre: variáveis entram como CHIPS pelo botão
 * "adicionar à fórmula", e entre os chips só se digitam números, operadores e
 * parênteses. A gramática e as mensagens estão em `formula.ts`; o servidor valida
 * a mesma coisa e é quem decide.
 *
 * Acessibilidade: tudo nativo. Cada trecho de texto é um `<input>` rotulado, e o
 * "×" do chip é um `<button>` que aparece no hover E no foco do teclado.
 *
 * 🔒 BR-04 — a PTAX é uma linha desta tabela e continua DIGITADA. Não existe e não
 * pode existir botão de consultar cotação aqui.
 */
import { useRef } from 'react'

import { Botao, Entrada } from '@/components/primitivos'

import {
  adicionarAFormula,
  adicionarLinha,
  alterarRascunho,
  alterarTexto,
  linhaPendente,
  removerDaFormula,
  removerLinha,
  salvarLinha,
  type EstadoDeParametros,
  type RascunhoDeVariavel,
} from './estadoDoEditor'

const COLUNAS = ['Tag', 'Nome', 'Descrição', 'Valor']

export function EditorDeParametros({
  estado,
  aoAlterar,
  editar,
  erro,
  contexto,
}: {
  estado: EstadoDeParametros
  aoAlterar: (proximo: EstadoDeParametros) => void
  editar: boolean
  /** A mensagem do `submit` bloqueado (variável pendente, fórmula inválida). */
  erro: string | null
  /** Distingue os rótulos quando há dois editores na tela (contrato × aditivo). */
  contexto: string
}) {
  // Onde o cursor estava na fórmula: o chip entra ali, e não sempre no fim.
  const cursor = useRef<{ indice: number; posicao: number } | null>(null)
  const idTitulo = `formula-${contexto.replace(/\W+/g, '-')}`

  const lembrarCursor = (indice: number) => (e: React.SyntheticEvent<HTMLInputElement>) => {
    cursor.current = { indice, posicao: e.currentTarget.selectionStart ?? 0 }
  }

  return (
    <div className="md:col-span-full flex flex-col gap-4">
      <div>
        <h4 className="type-body-lg text-text-primary font-semibold">Variáveis do contrato</h4>
        <p className="text-text-muted type-sm">
          valor vazio = fornecido pelo servidor no cálculo (ex. consumo do período) · valor
          preenchido = constante pactuada · a PTAX é digitada, nunca consultada na internet
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse" aria-label={`Variáveis do ${contexto}`}>
          <thead>
            <tr className="border-border-subtle border-b">
              {[...COLUNAS, ...(editar ? ['Ação'] : [])].map((rotulo) => (
                <th
                  key={rotulo}
                  scope="col"
                  className="text-text-muted px-2 py-2 text-left type-eyebrow"
                >
                  {rotulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {estado.linhas.length === 0 && (
              <tr>
                <td colSpan={editar ? 5 : 4} className="text-text-muted px-2 py-3 type-sm">
                  Nenhuma variável declarada.
                </td>
              </tr>
            )}
            {estado.linhas.map((linha) => {
              const nome = linha.salva?.tag ?? 'nova'
              if (!editar) {
                return (
                  <tr key={linha.chave} className="border-border-subtle border-b">
                    <td className="text-text-primary px-2 py-2 font-mono type-sm">
                      {linha.rascunho.tag}
                    </td>
                    <td className="text-text-body px-2 py-2 type-sm">{linha.rascunho.nome}</td>
                    <td className="text-text-body px-2 py-2 type-sm">{linha.rascunho.descricao}</td>
                    <td className="text-text-body px-2 py-2 type-sm">
                      <span aria-label={`valor da variável ${nome}`}>
                        {linha.salva?.valor === null || linha.salva?.valor === undefined
                          ? 'fornecido pelo servidor'
                          : linha.salva.valor.toLocaleString('pt-BR')}
                      </span>
                    </td>
                  </tr>
                )
              }

              const campo = (chave: keyof RascunhoDeVariavel) => ({
                value: linha.rascunho[chave],
                'aria-label': `${chave === 'descricao' ? 'descrição' : chave} da variável ${nome}`,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  aoAlterar(alterarRascunho(estado, linha.chave, chave, e.target.value)),
              })
              const pendente = linhaPendente(linha)

              return (
                <tr key={linha.chave} className="border-border-subtle border-b align-top">
                  <td className="px-2 py-2">
                    <Entrada
                      className="font-mono"
                      placeholder="ex. cotacao_ptax"
                      {...campo('tag')}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Entrada placeholder="ex. PTAX" {...campo('nome')} />
                  </td>
                  <td className="px-2 py-2">
                    <Entrada {...campo('descricao')} />
                  </td>
                  <td className="px-2 py-2">
                    <Entrada inputMode="decimal" placeholder="servidor" {...campo('valor')} />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-wrap items-center gap-1">
                      <Botao
                        variante="discreta"
                        icone="check"
                        aria-label={`salvar variável ${nome}`}
                        disabled={!pendente}
                        onClick={() => aoAlterar(salvarLinha(estado, linha.chave))}
                      >
                        salvar
                      </Botao>
                      <Botao
                        variante="discreta"
                        icone="plus"
                        aria-label={`adicionar ${nome} à fórmula`}
                        disabled={!linha.salva}
                        onClick={() => {
                          const c = cursor.current
                          const proximo = adicionarAFormula(
                            estado,
                            linha.chave,
                            c?.indice,
                            c?.posicao,
                          )
                          // Depois do chip inserido, o próximo entra logo após ele.
                          const indice =
                            c && proximo.segmentos[c.indice + 2] ? c.indice + 2 : undefined
                          cursor.current = indice !== undefined ? { indice, posicao: 0 } : null
                          aoAlterar(proximo)
                        }}
                      >
                        adicionar à fórmula
                      </Botao>
                      <Botao
                        variante="discreta"
                        icone="trash"
                        aria-label={`remover variável ${nome}`}
                        onClick={() => aoAlterar(removerLinha(estado, linha.chave))}
                      >
                        remover
                      </Botao>
                    </div>
                    {linha.erro && (
                      <p role="alert" className="text-danger-700 mt-1 type-sm">
                        {linha.erro}
                      </p>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {editar && (
        <div>
          <Botao
            variante="secundaria"
            icone="plus"
            onClick={() => aoAlterar(adicionarLinha(estado))}
          >
            adicionar variável
          </Botao>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <span id={idTitulo} className="type-label text-text-primary">
          Fórmula de cálculo (USD → BRL)
        </span>
        <div
          role="group"
          aria-labelledby={idTitulo}
          className={`rounded-field border-border-subtle flex min-h-[42px] flex-wrap items-center gap-1 border px-2 py-1.5 ${
            editar ? 'bg-surface-card focus-within:border-iox-navy' : 'bg-gray-50'
          }`}
        >
          {estado.segmentos.map((segmento, indice) => {
            if (segmento.tipo === 'variavel') {
              return (
                <span
                  key={indice}
                  className="group rounded-control bg-iox-navy text-text-inverse inline-flex items-center gap-1 px-2 py-0.5 font-mono type-sm"
                >
                  {segmento.tag}
                  {editar && (
                    <button
                      type="button"
                      aria-label={`remover ${segmento.tag} da fórmula`}
                      onClick={() => {
                        cursor.current = null
                        aoAlterar(removerDaFormula(estado, indice))
                      }}
                      className="text-text-inverse/70 hover:text-text-inverse opacity-0 duration-fast group-hover:opacity-100 focus-visible:opacity-100"
                    >
                      <i className="ph ph-x" aria-hidden="true" />
                    </button>
                  )}
                </span>
              )
            }
            if (!editar) {
              return (
                <span key={indice} className="text-text-body font-mono whitespace-pre type-sm">
                  {segmento.valor}
                </span>
              )
            }
            return (
              <input
                key={indice}
                aria-label={`trecho ${Math.floor(indice / 2) + 1} da fórmula`}
                value={segmento.valor}
                size={Math.max(2, segmento.valor.length + 1)}
                onChange={(e) => {
                  cursor.current = { indice, posicao: e.target.selectionStart ?? 0 }
                  aoAlterar(alterarTexto(estado, indice, e.target.value))
                }}
                onSelect={lembrarCursor(indice)}
                onFocus={lembrarCursor(indice)}
                className="text-text-primary min-w-0 bg-transparent font-mono outline-none type-sm"
              />
            )
          })}
        </div>
        {erro ? (
          <span role="alert" className="type-sm text-danger-700">
            {erro}
          </span>
        ) : (
          <span className="type-sm text-text-muted">
            {editar
              ? 'variáveis entram pelo botão "adicionar à fórmula"; entre elas, digite números, parênteses e + - * /'
              : 'aplicada na conversão pelo servidor'}
          </span>
        )}
      </div>
    </div>
  )
}
